import { Capacitor, registerPlugin } from '@capacitor/core';
import { AppLauncher } from '@capacitor/app-launcher';
import { EPASSI_APP_STORE_URL, EPASSI_PLAY_STORE_URL, NONCE_TTL_MS } from '../utils/epassi';

// Android-only custom plugin (AppEpassiPlugin.java): Epassi returns the wallet token
// via an activity result, which stock Capacitor plugins cannot receive.
interface AppEpassiPlugin {
  requestWalletToken(options: { url: string }): Promise<{ token: string }>;
  cancelWalletTokenRequest(): Promise<void>;
}

const AppEpassi = registerPlugin<AppEpassiPlugin>('AppEpassi');

export type EpassiLaunchResult =
  // iOS: the Epassi app was opened; the token arrives later via an appUrlOpen event.
  | { kind: 'launched' }
  // Android: the token was returned inline as an activity result.
  | { kind: 'token'; token: string }
  | { kind: 'cancelled' }
  | { kind: 'not_installed' };

const EPASSI_SCHEME_PROBE_URL = 'fi.epassi://';

// Bounds how long we wait for the Android native call: if the activity result is ever
// lost (e.g. the app's process was reclaimed mid-flow), this rejects instead of leaving
// the caller's promise — and the login button's loading state — hanging forever.
function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  onTimeout?: () => void
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      onTimeout?.();
      reject(new Error('Epassi wallet token request timed out'));
    }, timeoutMs);
    void (async () => {
      try {
        resolve(await promise);
      } catch (error) {
        reject(error);
      } finally {
        clearTimeout(timer);
      }
    })();
  });
}

export async function openEpassiWallet(deepLink: string): Promise<EpassiLaunchResult> {
  if (Capacitor.getPlatform() === 'android') {
    try {
      const { token } = await withTimeout(
        AppEpassi.requestWalletToken({ url: deepLink }),
        NONCE_TTL_MS,
        () => {
          void AppEpassi.cancelWalletTokenRequest();
        }
      );
      return { kind: 'token', token };
    } catch (error) {
      const code =
        typeof error === 'object' && error !== null ? (error as { code?: string }).code : undefined;
      if (code === 'CANCELLED') return { kind: 'cancelled' };
      if (code === 'NOT_INSTALLED') return { kind: 'not_installed' };
      throw error;
    }
  }

  // iOS: canOpenUrl requires the fi.epassi entry in LSApplicationQueriesSchemes.
  const { value: canOpen } = await AppLauncher.canOpenUrl({ url: EPASSI_SCHEME_PROBE_URL });
  if (!canOpen) return { kind: 'not_installed' };

  await AppLauncher.openUrl({ url: deepLink });
  return { kind: 'launched' };
}

// Opens the consumer Epassi app's store listing (the "app not installed" prompt).
export async function openEpassiStoreListing(): Promise<void> {
  const url = Capacitor.getPlatform() === 'android' ? EPASSI_PLAY_STORE_URL : EPASSI_APP_STORE_URL;
  await AppLauncher.openUrl({ url });
}
